import { Type, Expose, Exclude, Transform } from 'class-transformer';
import { DocumentSnapshot, QuerySnapshot } from 'firebase/firestore';
import { _FirebaseDocument, DocRef, toDocRef, anyToDate, forceRecord } from './Models';
import { Progression } from './Widget';
import { TDSectionSummary } from './td';

export type AssessmentStatus = 'draft' | 'scheduled' | 'running' | 'done' | 'failed' | 'stopped' | 'unknown'

export type AssessmentProgressStep = 'syncing' | 'ingesting' | 'auditing'

export type AssessmentKind =
    | 'standard'
    | 'internal_audit_questions_generation'
    | 'internal_audit_execution'
    | 'internal_audit_ccapa_prefill'
    | 'technical_documentation'

export type CoherenceCheckStatus = 'TODO' | 'RUNNING' | 'DONE' | 'FAILED'

export type TaskGroupingStatus = 'TODO' | 'RUNNING' | 'DONE' | 'FAILED' | 'UNKNOWN'

export class AssessmentProgress {
    @Expose()
    public step: AssessmentProgressStep
    @Expose()
    public percentage: number = 0.0

    static STEP: { SYNCING: AssessmentProgressStep, INGESTING: AssessmentProgressStep, AUDITING: AssessmentProgressStep } = {
        SYNCING: "syncing",
        INGESTING: "ingesting",
        AUDITING: "auditing",
    }
}

export class AssessmentDocuments {
    @Expose()
    @Type(() => DocRef)
    public procedures: DocRef[] = []
    @Expose()
    @Type(() => DocRef)
    public quality_manual: DocRef | null = null
}


export class TopicSummary {
    @Expose()
    public name: string
    @Expose()
    @Type(() => Ratings)
    public ratings: Ratings
    @Expose()
    @Type(() => DocRef)
    public topic_ref: DocRef
}

export class AssessmentJobsCounts {
    @Expose()
    public todo: number = 0
    @Expose()
    public running: number = 0
    @Expose()
    public done: number = 0
    @Expose()
    public failed: number = 0
    @Expose()
    public unknown: number = 0
    @Expose()
    public total: number = 0
}

@Exclude()
export class AssessmentResult extends _FirebaseDocument {
    @Expose()
    @Type(() => Clause)
    public clause: Clause
    @Expose()
    @Type(() => Ratings)
    public ratings: Ratings
    @Expose()
    public standard: string
    @Expose()
    @Type(() => TopicSummary)
    public topics_summary: TopicSummary[]
    @Expose()
    @Type(() => AssessmentResultTaskSummary)
    public tasks_summary?: AssessmentResultTaskSummary[] | null
    @Expose()
    @Type(() => AssessmentResultTaskCounts)
    public tasks_counts?: AssessmentResultTaskCounts | null


    collection(): string {
        return "results";
    }

    static fromFirebase(o: DocumentSnapshot): AssessmentResult | null {
        return _FirebaseDocument._fromFirebase(AssessmentResult, o);
    }
}

export class Assessment extends _FirebaseDocument {
    @Expose()
    public status: AssessmentStatus = 'draft'
    @Expose()
    public stop_requested?: boolean = false
    // MYAC-796 step 2 — a chat-triggered refresh may carry an extra ask the chat
    // auto-continues with once the run finishes; consumed = already auto-sent (fire once).
    @Expose()
    public chat_followup?: string | null = null
    @Expose()
    public chat_followup_consumed?: boolean = false
    @Expose()
    @Type(() => DocRef)
    public clauses: DocRef[]
    @Expose()
    @Type(() => AssessmentDocuments)
    public documents: AssessmentDocuments
    @Expose()
    @Type(() => AssessmentJobsCounts)
    public jobs_counts?: AssessmentJobsCounts | null
    @Expose()
    public assessed_standards_human_readable: string[] = []
    @Expose()
    public mac_score: number | null = null
    @Exclude()
    public result_summaries: AssessmentResult[]
    @Exclude()
    public _ratings: Ratings
    @Expose()
    @Type(() => Ratings)
    public aggregate_ratings?: Ratings | null
    @Expose()
    @Type(() => AssessmentResultTaskCounts)
    public aggregate_tasks_counts?: AssessmentResultTaskCounts | null
    @Expose()
    public disable_cloud_run_job?: boolean | null
    @Expose()
    public force_new_aqp: boolean | null = false
    @Expose()
    @Type(() => AssessmentProgress)
    public progress?: AssessmentProgress | null
    @Expose()
    public completeness?: 'full' | 'partial' | null
    @Expose()
    public kind?: AssessmentKind | null
    @Expose()
    public sections?: string[]
    @Expose()
    @Transform(toDocRef)
    public created_by?: DocRef | null
    @Expose()
    public coherence_check_status?: CoherenceCheckStatus | null
    @Expose()
    public enable_coherence_check?: boolean | null
    @Expose()
    @Type(() => ReviewCountsSummary)
    public aggregate_review_counts?: ReviewCountsSummary | null
    @Expose()
    public task_grouping_status?: TaskGroupingStatus | null
    @Expose()
    public task_grouping_summary?: string | null
    @Expose()
    public task_grouping_error?: string | null
    @Expose()
    public directives_summary?: string | null
    @Expose()
    public error?: string | null

    // TD-specific aggregates (populated on td_latest pseudo-assessment)
    @Expose()
    @Transform(forceRecord(TDSectionSummary))
    public td_sections?: Record<string, TDSectionSummary>
    @Expose()
    public mac_score_previous?: number | null
    @Expose()
    public mac_score_progression?: Progression
    @Expose()
    @Transform(anyToDate)
    public mac_score_computed_at?: Date
    @Expose()
    public overall_rating?: string | null
    @Expose()
    @Type(() => Ratings)
    public overall_rating_counts?: Ratings | null
    @Expose()
    @Type(() => AssessmentResultTaskCounts)
    public aggregate_proposed_tasks_counts?: AssessmentResultTaskCounts | null

    @Expose()
    public completeness_counts?: { found: number; not_found: number; not_applicable: number; absence_justified: number; TOTAL: number } | null

    @Expose()
    public consistency_counts?: { A: number; B: number; C: number; D: number; TOTAL: number } | null

    @Expose()
    public compliance_tasks_count?: number | null

    @Expose()
    public continuity_tasks_count?: number | null

    @Expose()
    public running_assessment_id?: string | null

    collection(): string {
        return "assessments";
    }

    get hasTaskGrouping(): boolean {
        return this.task_grouping_status === 'DONE'
    }

    get hasPendingTasks(): boolean {
        return (this.aggregate_review_counts?.pending ?? 0) > 0
    }

    get shouldDefaultToSummaryTab(): boolean {
        return this.hasTaskGrouping && this.hasPendingTasks
    }

    get mac_score_rounded(): number | null {
        if (this.mac_score === null) return null
        return Math.round(this.mac_score)
    }

    get project_path(): string {
        return this.parent_path;
    }

    get project_id(): string | null {
        const p = this.parent_path;
        if (!p) return null;
        const parts = p.split('/');
        return parts.length >= 2 ? parts[1] : null;
    }

    get procedures(): DocRef[] {
        return this.documents?.procedures ?? [];
    }

    get quality_manual(): DocRef | null {
        return this.documents?.quality_manual ?? null;
    }

    get ratings(): Ratings {
        if (!this._ratings) {
            this._ratings = Ratings.fromRatings(this.result_summaries.map(a => a.ratings));
        }
        return this._ratings;
    }

    get statusIcon() {
        switch (this.status) {
            case Assessment.STATUS.DRAFT:
                return "mdi-file-edit-outline"
            case Assessment.STATUS.SCHEDULED:
                return "mdi-clock-outline"
            case Assessment.STATUS.RUNNING:
                return "mdi-cogs"
            case Assessment.STATUS.DONE:
                return "mdi-check-outline"
            case Assessment.STATUS.FAILED:
                return "mdi-alert-outline"
            case Assessment.STATUS.UNKNOWN:
                return "mdi-help-circle-outline"
            default:
                return "mdi-alert-outline"

        }
    }

    static STATUS: { DRAFT: AssessmentStatus, SCHEDULED: AssessmentStatus, RUNNING: AssessmentStatus, DONE: AssessmentStatus, FAILED: AssessmentStatus, STOPPED: AssessmentStatus, UNKNOWN: AssessmentStatus } = {
        DRAFT: "draft",
        SCHEDULED: "scheduled",
        RUNNING: "running",
        DONE: "done",
        FAILED: "failed",
        STOPPED: "stopped",
        UNKNOWN: "unknown",
    }

    static fromFirebase(o: DocumentSnapshot, r: QuerySnapshot | null): Assessment | null {
        const a = _FirebaseDocument._fromFirebase(Assessment, o)
        if (!a) return null
        if (r) {
            a.result_summaries = r.docs
                .map((d) => {
                    return AssessmentResult.fromFirebase(d)
                })
                .filter((d) => d !== null)
                .sort((a: AssessmentResult, b: AssessmentResult) => a.clause!.section?.localeCompare(b.clause?.section || ''))
        } else {
            a.result_summaries = []
        }
        return a
    }
}


/**
 * The rating distribution shape: the five letters plus their total.
 *
 * Extracted from `Ratings` so a PLAIN object of the same shape is a first-class
 * citizen — the requirements-assessment `views/` read-model emits exactly this
 * (client data contract §12.25: "views speak the front's existing component
 * vocabulary"), which is what lets `RatingBadges` render a view field with no
 * adapter. `Ratings` instances remain assignable everywhere this is expected.
 */
export interface RatingCounts {
    A: number;
    B: number;
    C: number;
    D: number;
    F: number;
    TOTAL: number;
}

export class Ratings implements RatingCounts {
    @Expose()
    public A: number = 0;
    @Expose()
    public B: number = 0;
    @Expose()
    public C: number = 0;
    @Expose()
    public D: number = 0;
    @Expose()
    public F: number = 0;
    @Expose()
    public TOTAL: number = 0;

    rating(rate: string): number {
        switch (rate) {
            case 'A':
                return this.A;
            case 'B':
                return this.B;
            case 'C':
                return this.C;
            case 'D':
                return this.D;
            case 'F':
                return this.F;
            case 'TOTAL':
                return this.TOTAL;
        }
        return 0;
    }

    contains(ratings: string[]): boolean {
        return ratings.some(r => this.rating(r) > 0);
    }

    static fromRatings(r: Ratings[] | undefined): Ratings {
        const ratings = new Ratings();
        if (!r) return ratings;
        r.forEach(rating => {
            ratings.A += rating.A;
            ratings.B += rating.B;
            ratings.C += rating.C;
            ratings.D += rating.D;
            ratings.F += rating.F;
        });
        return ratings;
    }

    static fromStrings(r: string[]): Ratings {
        const ratings = new Ratings();
        r.forEach(rating => {
            switch (rating) {
                case 'A':
                    ratings.A++;
                    break;
                case 'B':
                    ratings.B++;
                    break;
                case 'C':
                    ratings.C++;
                    break;
                case 'D':
                    ratings.D++;
                    break;
                case 'F':
                    ratings.F++;
                    break;
            }
        });
        return ratings;
    }
}

export class AssessmentResultTaskSummary {
    @Expose()
    public name: string
    @Expose()
    public priority: 'A' | 'B' | 'C' | 'D'
}

export class AssessmentResultTaskCounts {
    @Expose()
    public A: number = 0
    @Expose()
    public B: number = 0
    @Expose()
    public C: number = 0
    @Expose()
    public D: number = 0
    @Expose()
    public F: number = 0
    @Expose()
    public TOTAL: number = 0
}

export class ReviewCountsSummary {
    @Expose()
    public total: number = 0
    @Expose()
    public pending: number = 0
    @Expose()
    public accepted: number = 0
    @Expose()
    public rejected: number = 0
    @Expose()
    public todo: number = 0
    @Expose()
    public in_progress: number = 0
    @Expose()
    public done: number = 0
    @Expose()
    public cancelled: number = 0
}

@Exclude()
export class JobInput {
    @Expose()
    @Type(() => DocRef)
    quality_manual: DocRef | null
    @Expose()
    @Type(() => DocRef)
    procedure: DocRef | null
    @Expose()
    @Type(() => DocRef)
    aqp: DocRef[] | null
    @Expose()
    @Type(() => DocRef)
    jobs: DocRef[] | null
}

@Exclude()
export class Job extends _FirebaseDocument {
    @Expose()
    public status: string
    @Expose()
    public function: string
    @Expose()
    @Type(() => JobInput)
    public input: JobInput
    @Expose()
    @Type(() => DocRef)
    public prompts: DocRef[] | null

    collection(): string {
        return "jobs";
    }

    static fromFirebase(o: DocumentSnapshot): Job | null {
        return _FirebaseDocument._fromFirebase(Job, o);
    }
}

export class JobTree extends Job {
    @Type(() => JobTree)
    public childs: JobTree[]

    static fromJob(j: Job): JobTree {
        const jt = new JobTree()
        Object.assign(jt, j)
        jt.childs = []
        return jt
    }
}

export class Clause {
    @Expose()
    public section: string;
    @Expose()
    public subsection?: string;
    @Expose()
    public name: string;
}

export class ClauseRef extends DocRef {
    @Expose()
    public section: string;
    @Expose()
    public name: string;

    static create(id: string, section: string, name: string) {
        const c = new ClauseRef();
        const variant = "drJP7miXtqnmb0fOusyr"
        const version = "e5BVdnhOC3XiRB8D1N1k"
        c.id = id
        c.path = `/standards/iso13485/variants/${variant}/versions/${version}/clauseanalysis/${id}`
        c.section = section
        c.name = name
        return c
    }
}
